import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, ConfirmModal } from "@/components/ui";
import { SectionCard } from "@/components/common";
import { cn } from "@/lib/utils";
import type {
  ReplaceSupplierInvoiceInput,
  RevolutPayoutMode,
  SettlementFlowKind,
  SettlementLineRow,
  SettlementPayeeRow,
} from "@/services/adminSettlementService";
import { formatMoneyOrEmpty } from "@/lib/money";
import {
  buildSettlementWorkflowSteps,
  defaultActiveWorkflowStep,
  type SettlementWorkflowStepId,
  workflowStepUnavailableMessage,
} from "../settlementWorkflowSteps";
import type {
  SettlementSelectionTotals,
  SettlementSummaryEmailStats,
} from "../settlementUi";
import type { Submitting } from "./utils";
import { settlementStepListClass } from "./SettlementStepCard";
import { CommissionInvoiceStep } from "./steps/CommissionInvoiceStep";
import { PaymentStep } from "./steps/PaymentStep";
import { RecordCollectionStep } from "./steps/RecordCollectionStep";
import { SummaryEmailStep } from "./steps/SummaryEmailStep";
import { SupplierInvoiceStep } from "./steps/SupplierInvoiceStep";

type CurrencySelectionTotals = SettlementSelectionTotals & { currency: string };

function SelectionSummary({
  selectedCount,
  totals,
  showCollectible,
}: {
  selectedCount: number;
  totals: CurrencySelectionTotals;
  showCollectible: boolean;
}) {
  const { t } = useTranslation("payments");
  const empty = t("shared.emptyCell");
  const items: { key: string; label: string; value: string | number }[] = [
    {
      key: "count",
      label: t("settlements.detail.selectedCount"),
      value: selectedCount,
    },
    {
      key: "payable",
      label: t("settlements.detail.selectedPayable"),
      value: formatMoneyOrEmpty(totals.payable, totals.currency, empty),
    },
    {
      key: "invoiceable",
      label: t("settlements.detail.selectedInvoiceable"),
      value: formatMoneyOrEmpty(totals.invoiceable, totals.currency, empty),
    },
    ...(showCollectible
      ? [
          {
            key: "collectible",
            label: t("settlements.detail.selectedCollectible"),
            value: formatMoneyOrEmpty(totals.collectible, totals.currency, empty),
          },
        ]
      : []),
  ];

  return (
    <section
      aria-label={t("settlements.detail.selectionSummaryTitle")}
      className="rounded-lg border border-surface-200 bg-surface-50 px-4 py-3 dark:bg-surface-100/40"
    >
      <p className="text-xs font-medium text-surface-500">
        {t("settlements.detail.selectionSummaryTitle")}
      </p>
      <dl className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-3">
        {items.map((item) => (
          <div key={item.key} className="min-w-0">
            <dt className="truncate text-xs text-surface-500">{item.label}</dt>
            <dd
              className={cn(
                "mt-0.5 text-[15px] font-semibold tabular-nums tracking-tight",
                selectedCount > 0 ? "text-surface-900" : "text-surface-400",
              )}
            >
              {item.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function selectionKey(lines: SettlementLineRow[], flow: SettlementFlowKind | null): string {
  if (!flow || lines.length === 0) return "";
  return `${flow}:${lines.map((line) => line.id).sort().join(",")}`;
}

type SettlementActionStepsProps = {
  flow: SettlementFlowKind | null;
  selectedLines: SettlementLineRow[];
  hasWrite: boolean;
  busy: boolean;
  noSelection: boolean;
  readOnly: boolean;
  mixedSelection: boolean;
  selectedCount: number;
  submitting: Submitting;
  totals: CurrencySelectionTotals;
  summaryStats: SettlementSummaryEmailStats;
  payee: SettlementPayeeRow | null;
  blockingRevolutOpen: boolean;
  onSendSummary: () => void | Promise<void>;
  onRecordCollection: (data: { reference: string; note?: string }) => Promise<void>;
  onRecordSupplierInvoice: (data: {
    supplierInvoiceNumber: string;
    supplierInvoiceDate?: string;
    totalCents: number;
    file: File;
    note?: string;
  }) => void | Promise<void>;
  onReplaceSupplierInvoice: (data: ReplaceSupplierInvoiceInput) => Promise<unknown>;
  onViewSupplierInvoice?: (documentId: string) => void;
  onDownloadSupplierInvoice?: (documentId: string) => void;
  onMarkInvoiced: (note?: string) => Promise<void>;
  onMarkPaid: (data: { reference: string; note?: string }) => Promise<void>;
  onRevolutPayout: (data: {
    mode: RevolutPayoutMode;
    reference: string;
    note?: string;
  }) => Promise<void>;
};

type SettlementWorkflowStepsPanelProps = Omit<
  SettlementActionStepsProps,
  "hasWrite" | "mixedSelection" | "selectedCount" | "flow"
> & {
  flow: SettlementFlowKind;
};

function SettlementWorkflowStepsPanel({
  flow,
  selectedLines,
  busy,
  noSelection,
  readOnly,
  submitting,
  totals,
  summaryStats,
  payee,
  blockingRevolutOpen,
  onSendSummary,
  onRecordCollection,
  onRecordSupplierInvoice,
  onReplaceSupplierInvoice,
  onViewSupplierInvoice,
  onDownloadSupplierInvoice,
  onMarkInvoiced,
  onMarkPaid,
  onRevolutPayout,
}: SettlementWorkflowStepsPanelProps) {
  const { t } = useTranslation("payments");
  const { t: tCommon } = useTranslation("common");
  const workflowSteps = useMemo(
    () => buildSettlementWorkflowSteps(flow, selectedLines),
    [flow, selectedLines],
  );
  const workflowStepById = useMemo(
    () => new Map(workflowSteps.map((step) => [step.id, step])),
    [workflowSteps],
  );

  const [activeStepId, setActiveStepId] = useState<SettlementWorkflowStepId>(() =>
    defaultActiveWorkflowStep(workflowSteps),
  );
  const [forcedStepIds, setForcedStepIds] = useState<Set<SettlementWorkflowStepId>>(new Set());
  const [pendingStepId, setPendingStepId] = useState<SettlementWorkflowStepId | null>(null);

  const isStepActionable = useCallback(
    (stepId: SettlementWorkflowStepId) => {
      const step = workflowStepById.get(stepId);
      if (!step) return false;
      return step.available || forcedStepIds.has(stepId);
    },
    [forcedStepIds, workflowStepById],
  );

  const openStep = useCallback(
    (stepId: SettlementWorkflowStepId) => {
      if (readOnly) {
        setActiveStepId(stepId);
        return;
      }
      const step = workflowStepById.get(stepId);
      if (!step) return;
      if (step.available || forcedStepIds.has(stepId)) {
        setActiveStepId(stepId);
        return;
      }
      setPendingStepId(stepId);
    },
    [forcedStepIds, readOnly, workflowStepById],
  );

  const handleConfirmUnavailable = () => {
    if (!pendingStepId) return;
    setForcedStepIds((prev) => new Set([...prev, pendingStepId]));
    setActiveStepId(pendingStepId);
    setPendingStepId(null);
  };

  const stepCardProps = (stepId: SettlementWorkflowStepId) => {
    const step = workflowStepById.get(stepId);
    return {
      stepNumber: step?.index ?? 0,
      completed: step?.completed ?? false,
      expanded: activeStepId === stepId,
      onHeaderClick: () => openStep(stepId),
    };
  };

  return (
    <>
      <div className={settlementStepListClass}>
        {workflowSteps.some((step) => step.id === "summary") && (
          <SummaryEmailStep
            {...stepCardProps("summary")}
            flow={flow}
            busy={busy}
            noSelection={noSelection}
            disabled={readOnly || !isStepActionable("summary")}
            submitting={submitting === "summary"}
            summaryStats={summaryStats}
            onSend={onSendSummary}
          />
        )}

        {workflowSteps.some((step) => step.id === "collect") && (
          <RecordCollectionStep
            {...stepCardProps("collect")}
            busy={busy}
            noSelection={noSelection}
            disabled={readOnly || !isStepActionable("collect")}
            submitting={submitting === "collect"}
            collectibleCents={totals.collectible}
            onRecord={onRecordCollection}
          />
        )}

        {workflowSteps.some((step) => step.id === "supplier") && (
          <SupplierInvoiceStep
            {...stepCardProps("supplier")}
            busy={busy}
            noSelection={noSelection}
            disabled={readOnly || !isStepActionable("supplier")}
            submitting={submitting === "supplier" || submitting === "replaceSupplier"}
            invoiceableCents={totals.invoiceable}
            selectedLines={selectedLines}
            onRecord={onRecordSupplierInvoice}
            onReplace={onReplaceSupplierInvoice}
            onViewInvoice={onViewSupplierInvoice}
            onDownloadInvoice={onDownloadSupplierInvoice}
          />
        )}

        {workflowSteps.some((step) => step.id === "invoice") && (
          <CommissionInvoiceStep
            {...stepCardProps("invoice")}
            busy={busy}
            noSelection={noSelection}
            disabled={readOnly || !isStepActionable("invoice")}
            submitting={submitting === "invoice"}
            invoiceableCents={totals.invoiceable}
            onMarkInvoiced={onMarkInvoiced}
          />
        )}

        {workflowSteps.some((step) => step.id === "pay") && (
          <PaymentStep
            {...stepCardProps("pay")}
            busy={busy}
            noSelection={noSelection}
            disabled={readOnly || !isStepActionable("pay")}
            submittingPay={submitting === "pay"}
            submittingRevolut={submitting === "revolut"}
            payableCents={totals.payable}
            payee={payee}
            blockingRevolutOpen={blockingRevolutOpen}
            onMarkPaid={onMarkPaid}
            onRevolutPayout={onRevolutPayout}
          />
        )}
      </div>

      <ConfirmModal
        closeLabel={tCommon("close")}
        isOpen={pendingStepId !== null}
        onClose={() => setPendingStepId(null)}
        onConfirm={handleConfirmUnavailable}
        title={t("settlements.detail.steps.unavailableTitle")}
        message={
          pendingStepId ? workflowStepUnavailableMessage(pendingStepId, t) : ""
        }
        cancelText={t("settlements.detail.steps.unavailableGoBack")}
        confirmText={t("settlements.detail.steps.unavailableConfirm")}
        isDestructive
      />
    </>
  );
}

export function SettlementActionSteps({
  flow,
  selectedLines,
  hasWrite,
  busy,
  noSelection,
  readOnly,
  mixedSelection,
  selectedCount,
  submitting,
  totals,
  summaryStats,
  payee,
  blockingRevolutOpen,
  onSendSummary,
  onRecordCollection,
  onRecordSupplierInvoice,
  onReplaceSupplierInvoice,
  onViewSupplierInvoice,
  onDownloadSupplierInvoice,
  onMarkInvoiced,
  onMarkPaid,
  onRevolutPayout,
}: SettlementActionStepsProps) {
  const { t } = useTranslation("payments");

  const showCollectible =
    flow === "RECEIPT_BASED" || mixedSelection || totals.collectible > 0;

  const selectionSignature = selectionKey(selectedLines, flow);

  return (
    <SectionCard
      title={t("settlements.detail.actionsPanelTitle")}
      description={t("settlements.detail.actionsPanelSubtitle")}
      className="xl:sticky xl:top-4"
      contentClassName="space-y-4"
    >
        <SelectionSummary
          selectedCount={selectedCount}
          totals={totals}
          showCollectible={showCollectible}
        />

        {mixedSelection ? (
          <Alert variant="warning" className="text-[13px]">
            {t("settlements.detail.mixedSelectionWarning")}
          </Alert>
        ) : null}

        {!hasWrite && (
          <p className="text-[13px] text-surface-500">{t("settlements.detail.readOnlyHint")}</p>
        )}

        {hasWrite && readOnly && (
          <p className="text-[13px] text-surface-500">
            {t("settlements.detail.finalizedReadOnlyHint")}
          </p>
        )}

        {hasWrite && flow && selectedLines.length > 0 && (
          <SettlementWorkflowStepsPanel
            key={selectionSignature}
            flow={flow}
            selectedLines={selectedLines}
            busy={busy}
            noSelection={noSelection}
            readOnly={readOnly}
            submitting={submitting}
            totals={totals}
            summaryStats={summaryStats}
            payee={payee}
            blockingRevolutOpen={blockingRevolutOpen}
            onSendSummary={onSendSummary}
            onRecordCollection={onRecordCollection}
            onRecordSupplierInvoice={onRecordSupplierInvoice}
            onReplaceSupplierInvoice={onReplaceSupplierInvoice}
            onViewSupplierInvoice={onViewSupplierInvoice}
            onDownloadSupplierInvoice={onDownloadSupplierInvoice}
            onMarkInvoiced={onMarkInvoiced}
            onMarkPaid={onMarkPaid}
            onRevolutPayout={onRevolutPayout}
          />
        )}

        {hasWrite && !flow && !mixedSelection && (
          <p className="text-[13px] text-surface-500">{t("settlements.detail.noActionsHint")}</p>
        )}

        {hasWrite && flow && noSelection && !mixedSelection && (
          <p className="text-[13px] text-surface-500">{t("settlements.detail.noActionsHint")}</p>
        )}
    </SectionCard>
  );
}
